import { isBadCoverUrl, resolveLocalAlbumCoverSync, pickCoverArtThumbHash } from './coverArt';
import { isRemoteCoverViewerShell, pickAlbumFolderPath } from '../lib/coverArtIPC';
import { pickLocalLibraryDisplayCover } from './localLibraryCoverPrecedence';

function extractLeadingNumber(str) {
  if (!str) return null;
  const m = str.match(/^0*(\d+)/);
  return m ? parseInt(m[1], 10) : null;
}

function inferTrackNum(t, fallback) {
  if (t.track_num != null && t.track_num > 0) return t.track_num;
  const fromTitle = extractLeadingNumber(t.title);
  if (fromTitle != null) return fromTitle;
  const filename = (t.file_path || '').split('/').pop() || '';
  const fromFile = extractLeadingNumber(filename);
  if (fromFile != null) return fromFile;
  return fallback;
}

/** Map local library album + track rows into AlbumDetailView shape. */
export function transformLocalAlbum(album, tracks) {
  const title = album.title || 'Untitled';

  const trackList = tracks || [];
  const embeddedHash = album.embedded_cover_hash
    || trackList.find((t) => t && t.embedded_cover_hash)?.embedded_cover_hash
    || null;
  const folderHash = album.folder_cover_hash
    || trackList.find((t) => t && t.folder_cover_hash)?.folder_cover_hash
    || null;
  const localCoverUrl = resolveLocalAlbumCoverSync(album);
  const rawOnlineCoverUrl = album.cover_art_url || '';
  const onlineCoverUrl = rawOnlineCoverUrl && !isBadCoverUrl(rawOnlineCoverUrl) ? rawOnlineCoverUrl : '';
  const hasLocalArtSource = !!(localCoverUrl || pickCoverArtThumbHash(album) || embeddedHash || folderHash || pickAlbumFolderPath(album));

  const overrideSource = album.user_override_source;
  const coverUrl = pickLocalLibraryDisplayCover({
    localCoverUrl,
    onlineCoverUrl,
    overrideSource,
    hasLocalArtSource,
    localResolutionComplete: !!localCoverUrl || !hasLocalArtSource,
    isRemoteTunnel: isRemoteCoverViewerShell(),
  }) || '';
  const artwork = coverUrl || ((embeddedHash || folderHash) ? '' : '');

  const mapped = trackList.map((t, i) => ({
    id: t.id || `local_${i}`,
    title: t.title || 'Unknown',
    artistName: t.artist || album.artist_name || 'Unknown',
    artist: t.artist || album.artist_name || 'Unknown',
    composer: t.composer || null,
    duration: Number(t.duration) || 0,
    duration_ms: (Number(t.duration) || 0) * 1000,
    trackNumber: inferTrackNum(t, i + 1),
    track_num: inferTrackNum(t, i + 1),
    discNumber: t.disc_num || 1,
    artworkUrl: artwork,
    source: 'local_library',
    albumId: album.album_key || album.id || null, album_key: album.album_key || album.id || null,
    file_path: t.file_path,
    machine_id: t.machine_id,
    format: t.format,
    bit_depth: t.bit_depth,
    sample_rate: t.sample_rate,
    channels: t.channels ?? null,
    embedded_cover_hash: t.embedded_cover_hash ?? null,
    folder_cover_hash: t.folder_cover_hash ?? null,
    maximum_bit_depth: t.bit_depth,
    maximum_sampling_rate: t.sample_rate ? t.sample_rate / 1000 : null,
  }));

  mapped.sort((a, b) => (a.discNumber - b.discNumber) || (a.trackNumber - b.trackNumber));

  return {
    id: album.album_key,
    title,
    artistName: album.artist_name || 'Unknown Artist',
    artist: album.artist_name || 'Unknown Artist',
    artworkUrl: artwork,
    cover: artwork,
    embedded_cover_hash: embeddedHash,
    folder_cover_hash: folderHash,
    cover_art_thumb_hash: album.cover_art_thumb_hash ?? null,
    user_override_source: album.user_override_source ?? null,
    cover_art_url: album.cover_art_url || null,
    label: album.label || null,
    release_date: album.release_date || null,
    enrichment_attempted_at: album.enrichment_attempted_at || null,
    year: album.year,
    genre: album.genre,
    genres: album.genre ? [{ id: album.genre, name: album.genre }] : [],
    description: album.artist_bio || '',
    bio: album.artist_bio || '',
    artist_biography: album.artist_bio || null,
    source: 'local_library',
    __source: 'local_library',
    isLocalLibrary: true,
    is_compilation: album.is_compilation,
    format: album.format,
    bit_depth: album.bit_depth,
    sample_rate: album.sample_rate,
    maximum_bit_depth: album.bit_depth,
    maximum_sampling_rate: album.sample_rate ? album.sample_rate / 1000 : null,
    album_key: album.album_key,
    machine_id: album.machine_id ?? null,
    sample_file_path: album.sample_file_path ?? mapped[0]?.file_path ?? null,
    library_folder_path: album.library_folder_path ?? null,
    mb_id: album.mb_id,
    musicbrainz_release_id: album.mb_id,
    qobuz_match_id: album.qobuz_match_id,
    tracks: mapped,
  };
}
